// jsxFileHandler.js
import React from 'react';
import * as Babel from '@babel/standalone';

/**
 * JSX File Handler
 * Handles parsing, compiling, and evaluating JSX files with imports
 */
class JsxFileHandler {
  constructor(componentRegistry) {
    this.componentRegistry = componentRegistry || {};
    this.importCache = new Map(); // Cache for processed imports
  }

  /**
   * Parse JSX string and separate imports from content
   * @param {string} jsxString - The JSX content to parse
   * @returns {Object} Object containing imports and content parts
   */
  parseJsxFile(jsxString) {
    if (!jsxString || typeof jsxString !== 'string') {
      return { imports: [], content: '', error: 'Invalid JSX string' };
    }

    try {
      // Extract import statements with regex
      const importRegex = /import\s+(?:{[\s\w,]+}|\*\s+as\s+\w+|\w+)\s+from\s+['"]([^'"]+)['"];?/g;
      const imports = [];
      let match;
      let content = jsxString;

      // Find all imports
      while ((match = importRegex.exec(jsxString)) !== null) {
        const fullStatement = match[0];
        const source = match[1];
        
        imports.push({
          statement: fullStatement,
          source
        });

        // Remove import from content
        content = content.replace(fullStatement, '');
      }

      // Clean up whitespace
      content = content.trim();
      
      return {
        imports,
        content,
        error: null
      };
    } catch (error) {
      console.error('Error parsing JSX file:', error);
      return {
        imports: [],
        content: jsxString,
        error: `Parse error: ${error.message}`
      };
    }
  }

  /**
   * Resolve imports to actual components
   * @param {Array} imports - Array of import information objects
   * @returns {Object} Object with resolved components
   */
  resolveImports(imports) {
    const resolvedComponents = { ...this.componentRegistry };

    imports.forEach(importInfo => {
      const { source } = importInfo;
      
      // Check if we've already processed this import
      if (this.importCache.has(source)) {
        const cachedComponents = this.importCache.get(source);
        Object.assign(resolvedComponents, cachedComponents);
        return;
      }
      
      // Handle various import sources
      if (source === 'react' || source === 'React') {
        // Already available
        console.log('React import - already available');
      } else if (source === 'qwanyx') {
        // Already available in registry
        console.log('Qwanyx import - components should be in registry');
      } else if (source.startsWith('./') || source.startsWith('../')) {
        console.warn(`Local module import not supported yet: ${source}`);
      } else {
        console.warn(`External module import not supported: ${source}`);
      }
    });

    return resolvedComponents;
  }

  /**
   * Transpile JSX content to JavaScript
   * @param {string} jsxContent - The JSX content without imports
   * @returns {Object} Object with transpiled code or error
   */
  transpileJsx(jsxContent) {
    try {
      // Wrap content in a function to create a proper scope
      const wrappedContent = `
        (() => {
          return (
            ${jsxContent}
          );
        })()
      `;
      
      // Transform JSX to JavaScript
      const result = Babel.transform(wrappedContent, {
        presets: ['react'],
        filename: 'dynamic-jsx.js',
      });
      
      return {
        code: result.code,
        error: null
      };
    } catch (error) {
      console.error('JSX transpilation error:', error);
      return {
        code: null,
        error: `Transpilation error: ${error.message}`
      };
    }
  }

  /**
   * Evaluate transpiled JSX with component context
   * @param {string} code - Transpiled JavaScript code
   * @param {Object} components - Component context for evaluation
   * @returns {React.ReactElement|null} The evaluated React element or error
   */
  evaluateJsx(code, components) {
    if (!code) return null;
    
    try {
      // Add React to components
      const context = {
        React,
        ...components
      };
      
      // Create function with component context in scope
      const evalFn = new Function(
        ...Object.keys(context),
        `return ${code}`
      );
      
      // Execute with components as arguments
      return evalFn(...Object.values(context));
    } catch (error) {
      console.error('JSX evaluation error:', error);
      return (
        <div style={{ color: 'red', padding: '10px', border: '1px solid red' }}>
          <h4>JSX Evaluation Error:</h4>
          <p>{error.message}</p>
        </div>
      );
    }
  }

  /**
   * Process JSX string completely - parse, transpile, evaluate
   * @param {string} jsxString - The full JSX string to process
   * @returns {Object} Object with result and any errors
   */
  processJsx(jsxString) {
    // Step 1: Parse the JSX file
    const { imports, content, error: parseError } = this.parseJsxFile(jsxString);
    
    if (parseError) {
      return {
        element: this.createErrorElement(`Parse error: ${parseError}`),
        success: false,
        error: parseError
      };
    }
    
    // Step 2: Resolve imports
    const components = this.resolveImports(imports);
    
    // Step 3: Transpile JSX
    const { code, error: transpileError } = this.transpileJsx(content);
    
    if (transpileError) {
      return {
        element: this.createErrorElement(`Transpile error: ${transpileError}`),
        success: false,
        error: transpileError
      };
    }
    
    // Step 4: Evaluate the code
    try {
      const element = this.evaluateJsx(code, components);
      return {
        element,
        success: true,
        error: null
      };
    } catch (evalError) {
      return {
        element: this.createErrorElement(`Evaluation error: ${evalError.message}`),
        success: false,
        error: evalError
      };
    }
  }
  
  /**
   * Create an error element for display
   * @param {string} message - The error message
   * @returns {React.ReactElement} Error element
   */
  createErrorElement(message) {
    return (
      <div style={{ 
        padding: '15px', 
        border: '1px solid #ff5555', 
        borderRadius: '4px',
        backgroundColor: '#ffeeee',
        color: '#cc0000',
        margin: '10px 0'
      }}>
        <h4 style={{ margin: '0 0 10px 0' }}>JSX Rendering Error</h4>
        <p style={{ margin: '0' }}>{message}</p>
      </div>
    );
  }
}

export default JsxFileHandler;